import React, { useState, useEffect } from 'react'
import './Configs.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { useNavigate, useParams } from 'react-router-dom'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ConfigReview from './ConfigReview/ConfigReview'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import ICSDataGrid from '../../components/ICSDataGrid/ICSDataGrid'
import { useSelector, useDispatch } from 'react-redux'
import { DefaultState, IPAConfig } from '../../types'
import { Container, Col, Row, ModalBody } from 'reactstrap'
import {
  getConfig,
  getConfigs,
  removeConfig,
  setEntityError,
  getConfigFile,
  downloadConfigFile,
} from '../../redux/slices'
import { createEntityAdapter } from '@reduxjs/toolkit'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import * as Types from '../../types'
import { ICSModal } from '../../components'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import ConfigSave from './ConfigSave/ConfigSave'
import { AppDispatch } from '../../services/lib'

const Configs = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const isAdmin: boolean | undefined = useSelector(
    (state: DefaultState) => state.session.data.user?.roles?.includes('admin')
  )
  const { scriptId } = useParams() as { scriptId: string }
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false)
  const configsState = useSelector((state: DefaultState) => state.configs)
  const configsError = useSelector((state: DefaultState) => state.configs.error)
  const configsArray = createEntityAdapter()
    .getSelectors()
    .selectAll(configsState) as IPAConfig[]

  useEffect(() => {
    if (!scriptId) return navigate('/ui2/scripts')
    const initialData = async () => {
      await dispatch(getConfigs(scriptId))
    }

    initialData()
  }, [dispatch, navigate, scriptId])

  function hideModal() {
    setModalVisible(false)
  }

  function hideUploadModal() {
    dispatch(
      setEntityError({
        sliceName: 'configs',
        entityName: 'Config',
        error: null,
      })
    )
    setUploadModalVisible(false)
  }

  const download = async (data: Types.IPAConfig) => {
    const configFileRes = await dispatch(
      getConfigFile({
        scriptId,
        configId: data.id,
      })
    )

    if (configFileRes.type.split('/').pop() === 'fulfilled') {
      const downloadRes = await dispatch(
        downloadConfigFile({
          data: (configFileRes.payload as Types.IPAConfig).file,
          fileName: data.name,
        })
      )

      downloadRes.type.split('/').pop() === 'fulfilled' &&
        dispatch(getConfigs(scriptId))
    }
  }

  return (
    <>
      <Container fluid={true}>
        <Row>
          <Col>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Scripts',
                  url: '/ui2/scripts/',
                },
                {
                  title: 'Configurations',
                },
              ]}
              help={{
                title: 'Script Configurations',
                text: `Configurations are optional for Analyzers but can be used to configure the operation of an Analysis Script based on parameters contained within. It could be as simple as passing a parameter to make a different method in the script run or as complex as to dictate what to search for in the data. For more information on how to apply configurations see the Integrated Post Analysis documentation or examples.`,
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }}>
            <ComponentBox>
              {configsError?.code === 'error' && !uploadModalVisible ? (
                <ICSErrorCard
                  code={configsError.code}
                  message={configsError.message}
                  fields={configsError.fields}
                />
              ) : (
                <ICSDataGrid
                  dataSource={configsArray}
                  columnOptions={[
                    {
                      type: 'buttons',
                      caption: 'Action',
                      width: 150,
                      buttons: [
                        {
                          id: 'btn',
                          hint: 'View',
                          icon: 'find',
                          onClick: function review(e) {
                            dispatch(
                              getConfig({
                                scriptId: scriptId,
                                configId: e.row.data.id,
                              })
                            )
                            setModalVisible(true)
                            dispatch(getConfigs(scriptId))
                          },
                        },
                        {
                          hint: 'Delete',
                          icon: 'trash',
                          onClick: function del(e) {
                            dispatch(
                              removeConfig({
                                sId: scriptId,
                                cId: e.row.data.id,
                              })
                            )
                            dispatch(getConfigs(scriptId))
                          },
                        },
                        {
                          hint: 'Download',
                          icon: 'download',
                          onClick: async e => await download(e.row.data),
                        },
                      ].filter(button => isAdmin || button.hint !== 'Delete'),
                    },
                    { dataField: 'name', caption: 'Name' },
                    {
                      dataField: 'description',
                      caption: 'Description',
                      cellRender: data => {
                        return (
                          <ICSDataCellDetailView
                            {...data}
                            modalWidth='35vw'
                            modalHeight='20vh'
                          />
                        )
                      },
                    },
                    { dataField: 'user.name', caption: 'User' },
                    { dataField: 'created_at', caption: 'Date Created' },
                    { dataField: 'updated_at', caption: 'Last Updated' },
                  ]}
                  options={{
                    buttons: [
                      <ICSActionButton
                        id='configUploadButton'
                        type='success'
                        height={35}
                        stylingMode='contained'
                        onClick={() => {
                          setUploadModalVisible(true)
                        }}
                        text='Add'
                      >
                        <span className='glyphicons glyphicons-floppy-disk'></span>
                        <span className='glyphicons glyphicons-plus'></span>
                      </ICSActionButton>,
                    ],
                  }}
                />
              )}
              <ICSModal
                visible={uploadModalVisible}
                onHiding={hideUploadModal}
                title='Add a Configuration'
              >
                <ModalBody>
                <span>
                  {uploadModalVisible && (
                    <ConfigSave
                      hideModal={hideUploadModal}
                      scriptId={scriptId}
                    />
                  )}
                </span>
                </ModalBody>

              </ICSModal>

              <ICSModal
                visible={modalVisible}
                onHiding={hideModal}
                title='Configuration Viewer'
                defaultWidth='95vw'
                defaultHeight='95vh'
              >
                <ModalBody>
                <span>
                  {modalVisible && (
                    <ConfigReview
                      script={scriptId}
                      setVisible={setModalVisible}
                    />
                  )}
                </span>
                </ModalBody>
              </ICSModal>
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}
export default Configs
